import { useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { getZegoToken } from "../../../services/api/zego.api";

const VideoCallWrapper = styled(Box)<{}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  transition: all 0.3s ease-in-out;
`;

const MainContainer = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  width: 100%;
`;

export default function ModeratorTalentStreamViewer({
  userId,
  videoId,
  displayName,
  username,
  bookingId,
  zg,
}: {
  userId: string;
  videoId: string;
  displayName: string;
  username?: string;
  bookingId: string;
  zg: ZegoExpressEngine | null;
}) {
  const [videoToken, setVideoToken] = useState<string | null>(null);
  const [error, setError] = useState("");

  const [isJoined, setIsJoined] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(
    new Map()
  );
  const [remoteStreamInfo, setRemoteStreamInfo] = useState<
    Map<string, { userName: string; streamID: string }>
  >(new Map());

  console.log("remoteStreams zg ", zg);
  console.log("remoteStreams zg streams", remoteStreams);
  console.log("remoteStreams zg streamInfo", remoteStreamInfo);

  const mainVideoRef = useRef<HTMLVideoElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const applyStreamToVideo = (
    videoElement: HTMLVideoElement | null,
    stream: MediaStream | null
  ) => {
    if (videoElement && stream) {
      videoElement.srcObject = stream;
      videoElement.play().catch(console.error);
    }
  };

  useEffect(() => {
    if (videoToken) return;
    const token = localStorage.getItem("videoToken");
    if (token) {
      setVideoToken(token);
    } else {
      getZegoToken(userId!)
        .then((token: string) => {
          setVideoToken(token);
        })
        .catch((err: any) => {
          setError(err.message || "Failed to fetch token");
        });
    }
  }, [videoId, videoToken, userId]);

  const joinRoom = async () => {
    try {
      if (isJoined || !zg || !videoToken) return;

      await zg.loginRoom(
        videoId,
        videoToken,
        { userID: userId, userName: displayName || userId },
        { userUpdate: true }
      );

      setIsJoined(true);
    } catch (error) {
      console.error("Join room error:", error);
      setError("Failed to join room");
    }
  };

  useEffect(() => {
    if (!zg) return;

    const handleRoomStreamUpdate = async (
      roomID: string,
      updateType: string,
      streamList: any[]
    ) => {
      console.log("Stream update:", updateType, streamList);

      if (updateType === "ADD") {
        try {
          for (const streamInfo of streamList) {
            const stream = await zg.startPlayingStream(streamInfo.streamID);

            if (stream) {
              setRemoteStreams(
                (prev) => new Map(prev.set(streamInfo.streamID, stream))
              );
              setRemoteStreamInfo(
                (prev) =>
                  new Map(
                    prev.set(streamInfo.streamID, {
                      userName:
                        streamInfo.userName || `User ${streamInfo.streamID}`,
                      streamID: streamInfo.streamID,
                    })
                  )
              );
              console.log(`Remote stream added: ${streamInfo.streamID}`);
            }
          }
        } catch (error) {
          console.error("Error playing remote stream:", error);
        }
      } else if (updateType === "DELETE") {
        for (const streamInfo of streamList) {
          setRemoteStreams((prev) => {
            const newMap = new Map(prev);
            newMap.delete(streamInfo.streamID);
            return newMap;
          });
          setRemoteStreamInfo((prev) => {
            const newMap = new Map(prev);
            newMap.delete(streamInfo.streamID);
            return newMap;
          });
          zg.stopPlayingStream(streamInfo.streamID);
          console.log(`Remote stream removed: ${streamInfo.streamID}`);
        }
      }
    };

    zg.on("roomStreamUpdate", handleRoomStreamUpdate);

    return () => {
      zg.off("roomStreamUpdate", handleRoomStreamUpdate);
    };
  }, [zg]);

  useEffect(() => {
    if (remoteStreams.size > 0) {
      const firstStream = remoteStreams.values().next().value;
      if (firstStream) {
        applyStreamToVideo(mainVideoRef.current, firstStream);
      }
    }
  }, [remoteStreams]);

  const cleanup = () => {
    if (zg && isJoined) {

      if (remoteStreams.size > 0) {
        for (const stream of remoteStreams.values()) {
          stream.getTracks().forEach((track) => track.stop());
        }
        for (const streamInfo of remoteStreamInfo.values()) {
          zg.stopPlayingStream(streamInfo.streamID);
        }
      }

      zg.logoutRoom(videoId);
      setIsJoined(false);
      setRemoteStreams(new Map());
      setRemoteStreamInfo(new Map());
    }
  };

  useEffect(() => {
    if (zg && videoToken) joinRoom();

    return () => {
      cleanup();
    };
    // eslint-disable-next-line
  }, [zg, videoToken]);

  // Render video content based on mode
  const renderVideoContent = () => {

    // Show live video call - Updated to use MultiStreamGrid
    if (remoteStreams.size > 0) {
      return (
        <>
          {/* Multiple remote streams in grid layout */}
          <MultiStreamGrid
            streams={remoteStreams}
            streamInfo={remoteStreamInfo}
          />

        </>
      );
    }

    // Waiting state
    return (
      <Box
        style={{
          height: "100%",
          width: "100%",
          backgroundColor: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h6"
          align="center"
          style={{
            color: "white",
          }}
        >
          "Waiting for host to join..."
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <MainContainer>
        <VideoCallWrapper
          sx={{
            position: "relative",
            top: "auto",
            left: "auto",
            height: "auto",
            zIndex: "auto",
            backgroundColor: "transparent",
            width: "100%",
            opacity: 1,
            transition: "all 0.3s ease-in-out",
          }}
        >
          {/* Main Video Section */}
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              height: isMobile ? "50vh" : "80vh",
              backgroundColor: "transparent",
              position: "relative",
            }}
          >
            {renderVideoContent()}
          </Paper>
        </VideoCallWrapper>
      </MainContainer>
    </>
  );
}

// Component to display multiple remote streams in a grid
const MultiStreamGrid = ({
  streams,
  streamInfo,
}: {
  streams: Map<string, MediaStream>;
  streamInfo: Map<string, { userName: string; streamID: string }>;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const streamArray = Array.from(streams.entries());

  // Determine grid layout based on number of streams and screen size
  const getGridLayout = () => {
    const count = streamArray.length;

    if (isMobile) {
      return count === 1 ? "1fr" : count === 2 ? "1fr 1fr" : "1fr 1fr";
    }

    if (isTablet) {
      if (count === 1) return "1fr";
      if (count === 2) return "1fr 1fr";
      if (count === 3) return "1fr 1fr";
      return "1fr 1fr";
    }

    // Desktop layout
    if (count === 1) return "1fr";
    if (count === 2) return "1fr 1fr";
    if (count === 3) return "1fr 1fr 1fr";
    if (count === 4) return "1fr 1fr";
    return "1fr 1fr 1fr";
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: getGridLayout(),
        gap: 2,
        width: "100%",
        height: "100%",
        p: 2,
      }}
    >
      {streamArray.map(([streamID, stream]) => {
        const info = streamInfo.get(streamID);
        return (
          <StreamVideo
            key={streamID}
            stream={stream}
            streamID={streamID}
            userName={info?.userName || `User ${streamID}`}
            isMobile={isMobile}
          />
        );
      })}
    </Box>
  );
};

// Individual stream video component
const StreamVideo = ({
  stream,
  streamID,
  userName,
  isMobile,
}: {
  stream: MediaStream;
  streamID: string;
  userName: string;
  isMobile: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Apply stream to video element
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(console.error);
    }
  }, [stream, streamID]);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#000",
        minHeight: isMobile ? "200px" : "300px",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          backgroundColor: "#000",
        }}
      />
    </Box>
  );
};
